import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Text,
  Button,
  Alert,
  ActionIcon,
  Tooltip,
  Badge,
  Loader,
} from "@mantine/core";
import { IconDownload, IconPrinter } from "@tabler/icons-react";
import { primary, success, warning } from "../../../constants/color-constants";

// Status type definition
export type StatusType =
  | "Open"
  | "Closed"
  | "Pending"
  | "In Progress"
  | "In-Progress"
  | "Completed"
  | "Query Agreed"
  | "Query Disagreed"
  | "Unable to Determine"
  | "Other"
  | "Unknown Status";

interface HtmlViewerProps {
  htmlContent: string;
  filename?: string;
  queryId?: string;
  status?: StatusType | string;
  onDownload?: () => void | Promise<void>;
  onError?: (error: string) => void;
  onClose?: () => void;
  onPrint?: () => void | Promise<void>;
  downloadLoading?: boolean;
  printLoading?: boolean;
}

export const HtmlViewer: React.FC<HtmlViewerProps> = ({
  htmlContent,
  filename,
  queryId,
  status,
  onDownload,
  onError,
  onClose,
  onPrint,
  downloadLoading = false,
  printLoading = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [processedHtml, setProcessedHtml] = useState<string>("");

  const handleError = useCallback(
    (errorMessage: string) => {
      setError(errorMessage);
      setLoading(false);
      if (onError) {
        onError(errorMessage);
      }
    },
    [onError]
  );

  // Process and validate HTML content
  const processHtml = useCallback(async () => {
    if (!htmlContent) {
      handleError("No HTML content provided");
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Simple validation to check if content looks like HTML
      if (!htmlContent.trim()) {
        handleError("Empty HTML content provided");
        return;
      }

      // Optional: Sanitize or process the HTML content here if needed
      // For now, we'll use it as-is
      setProcessedHtml(htmlContent);
      setLoading(false);
    } catch (err) {
      console.error("Error processing HTML:", err);
      handleError("Failed to process HTML content");
    }
  }, [htmlContent, handleError]);

  // Process HTML when component mounts or content changes
  useEffect(() => {
    processHtml();
  }, [processHtml]);

  // Helper function to render status badge with appropriate colors
  const renderStatusBadge = (status: StatusType | string) => {
    let color: string;
    let displayStatus: string;

    // Normalize the status to handle case variations
    const normalizedStatus = status.toLowerCase().trim();

    // Default colors mapping (case-insensitive)
    const defaultColors: Record<string, string> = {
      open: "#28a745",
      closed: "#dc3545",
      pending: "#ffc107",
      "in-progress": "#2a67f6",
      completed: success[6],
      "unable to determine": warning[4],
      other: primary[5],
      "alternate response": primary[5],
      "query not answered": primary[5],
      "query disagreed": "#dc2626",
      "query agreed": success[6],
    };

    // Check if this is a query status that should display as "Completed"
    if (status) {
      displayStatus = status;
      color = defaultColors[normalizedStatus];
    } else {
      // For all other statuses, use the original status
      displayStatus = status;

      // Try to find color using normalized status first, then original status
      color =
        defaultColors[normalizedStatus] || defaultColors[status] || "#6c757d";
    }

    return (
      <Box style={{ minWidth: "200px" }}>
        <Badge
          fullWidth={true}
          color={color}
          variant='light'
          radius='sm'
          size='md'
          style={{
            border: `1px solid ${color}`,
            height: "26px",
            lineHeight: "30px",
            padding: "0px 4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          {displayStatus}
        </Badge>
      </Box>
    );
  };

  if (error) {
    return (
      <Alert color='red' title='HTML Load Error'>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <style>
        {`
  
  

  
  .html-scroll-container:hover::-webkit-scrollbar-thumb {
    background: #blue;
  }

          
       

          .html-content {
            width: 100%;
            min-width: 100%;
            max-width: 100%;
            background: white;
            border-radius: 4px;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
            margin: 0;
            line-height: 1.4;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: clamp(10px, 2.5vw, 14px); /* Fluid font size */
            color: #333;
            overflow-wrap: break-word;
            word-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
            box-sizing: border-box;
          }

          .html-content:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          }

          /* Reset margins for better fitting */
          .html-content * {
            max-width: 100% !important; /* Force all elements to respect container width */
            box-sizing: border-box;
          }

          .html-content h1 {
            font-size: clamp(14px, 4vw, 22px);
            margin: clamp(4px, 1vw, 8px) 0 clamp(3px, 0.8vw, 6px) 0;
            color: #1a1a1a;
            font-weight: 600;
            line-height: 1.2;
          }

          .html-content h2 {
            font-size: clamp(12px, 3.5vw, 19px);
            margin: clamp(3px, 0.8vw, 6px) 0 clamp(2.5px, 0.7vw, 5px) 0;
            color: #1a1a1a;
            font-weight: 600;
            line-height: 1.2;
          }

          .html-content h3 {
            font-size: clamp(11px, 3vw, 17px);
            margin: clamp(2.5px, 0.7vw, 5px) 0 clamp(2px, 0.6vw, 4px) 0;
            color: #1a1a1a;
            font-weight: 600;
            line-height: 1.2;
          }

          .html-content h4, .html-content h5, .html-content h6 {
            font-size: clamp(10px, 2.8vw, 15px);
            margin: clamp(2px, 0.6vw, 4px) 0 clamp(1.5px, 0.5vw, 3px) 0;
            color: #1a1a1a;
            font-weight: 600;
            line-height: 1.2;
          }

          .html-content p {
            margin: 0 0 clamp(3px, 0.8vw, 6px) 0;
            color: #333;
            line-height: 1.4;
          }

          .html-content ul, .html-content ol {
            margin: clamp(2px, 0.6vw, 4px) 0 clamp(3px, 0.8vw, 6px) 0;
            padding-left: clamp(12px, 3vw, 20px);
          }

          .html-content li {
            margin-bottom: clamp(1px, 0.3vw, 2px);
            line-height: 1.3;
          }

          .html-content table {
            width: 100%;
            border-collapse: collapse;
            margin: clamp(3px, 0.8vw, 6px) 0;
            font-size: clamp(8px, 2.2vw, 13px);
            table-layout: fixed;
            word-wrap: break-word;
          }

          .html-content thead, .html-content tbody, .html-content tr {
            display: table;
            width: 100%;
            table-layout: fixed;
          }

          .html-content th, .html-content td {
            border: 1px solid #ddd;
            padding: clamp(2px, 0.8vw, 6px) clamp(3px, 1vw, 8px);
            text-align: left;
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
            white-space: normal;
            overflow: hidden;
            vertical-align: top;
          }

          .html-content th {
            background-color: #f8f9fa;
            font-weight: 600;
            font-size: clamp(7px, 2vw, 12px);
          }

          .html-content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 6px auto;
            border-radius: 3px;
          }

          .html-content pre {
            background: #f5f5f5;
            border: 1px solid #ddd;
            border-radius: 3px;
            overflow-x: auto;
            font-size: clamp(8px, 2vw, 12px);
            margin: clamp(3px, 0.8vw, 6px) 0;
            padding: clamp(4px, 1.2vw, 8px);
            white-space: pre-wrap;
            word-wrap: break-word;
            max-width: 100%;
            line-height: 1.3;
          }

          .html-content code {
            background: #f5f5f5;
            padding: clamp(1px, 0.2vw, 2px) clamp(2px, 0.5vw, 3px);
            border-radius: 2px;
            font-size: clamp(8px, 2vw, 12px);
          }

          .html-content blockquote {
            border-left: 3px solid #ddd;
            margin: clamp(3px, 0.8vw, 6px) 0;
            padding: clamp(2px, 0.6vw, 4px) clamp(6px, 1.5vw, 12px);
            background: #f9f9f9;
            font-style: italic;
          }

          .html-content div, .html-content section {
            margin-bottom: clamp(2px, 0.6vw, 4px);
          }

          /* Handle long URLs and text */
          .html-content a {
            word-break: break-all;
            color: #2a67f6;
            text-decoration: none;
            max-width: 100%;
            overflow-wrap: break-word;
            font-size: inherit;
          }

          .html-content a:hover {
            text-decoration: underline;
          }

          /* Container padding adjustments */
          @container (max-width: 600px) {
            .html-content {
              padding: clamp(8px, 2vw, 12px);
            }
          }

          @container (max-width: 400px) {
            .html-content {
              padding: clamp(6px, 1.5vw, 8px);
            }
          }

          /* Fallback media queries for older browsers */
          @media (max-width: 600px) {
            .html-content {
              padding: clamp(8px, 2vw, 12px);
            }
          }

          @media (max-width: 400px) {
            .html-content {
              padding: clamp(6px, 1.5vw, 8px);
            }
          }

          @media (max-width: 300px) {
            .html-content {
              padding: clamp(4px, 1vw, 6px);
            }
          }
        `}
      </style>

      <Box
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          overflow: "hidden",
          maxHeight: "85vh",
        }}
      >
        {/* Header with Query ID and Status */}
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "4px 8px",
            backgroundColor: "#ffffff",
            flexShrink: 0,
            borderBottom: "1px solid #e9ecef",
          }}
        >
          {/* Left side - Query ID (very starting) */}
          <Box style={{ display: "flex", alignItems: "center" }}>
            {queryId && (
              <Text
                size='xs'
                fw={600}
                style={{
                  color: "#495057",
                  fontSize: "12px",
                  lineHeight: "20px",
                }}
              >
                Query ID: {queryId}
              </Text>
            )}
          </Box>

          {/* Middle - Status Badge */}
          <Box
            mr={"20px"}
            style={{
              display: "flex",
              alignItems: "center",
              height: "30px",
              fontSize: "12px",
            }}
          >
            {status && renderStatusBadge(status)}
          </Box>

          {/* Right side - Action buttons */}
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              maxHeight: "25px",
            }}
          >
            {/* Print button */}
            <Tooltip label={printLoading ? "Printing..." : "Print"}>
              <ActionIcon
                variant='outline'
                size='sm'
                onClick={onPrint}
                disabled={printLoading}
                style={{
                  borderRadius: "4px",
                  height: "25px",
                  width: "25px",
                }}
              >
                {printLoading ? (
                  <Loader size={12} />
                ) : (
                  <IconPrinter size={14} />
                )}
              </ActionIcon>
            </Tooltip>

            {/* Download button */}
            {onDownload && (
              <Tooltip label={downloadLoading ? "Downloading..." : "Download"}>
                <ActionIcon
                  variant='outline'
                  size='sm'
                  onClick={onDownload}
                  disabled={downloadLoading}
                  style={{
                    borderRadius: "4px",
                    height: "25px",
                    width: "25px",
                  }}
                >
                  {downloadLoading ? (
                    <Loader size={12} />
                  ) : (
                    <IconDownload size={14} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}

            {/* Close button */}
            {onClose && (
              <Button
                variant='outline'
                size='compact-sm'
                onClick={onClose}
                h={"25px"}
                style={{
                  borderRadius: "4px",
                }}
              >
                Close
              </Button>
            )}
          </Box>
        </Box>

        {/* HTML Content Container */}
        <Box
          className='html-scroll-container'
          style={{
            flex: 1,
            backgroundColor: "#f8f9fa",
            overflow: "auto",
            width: "100%",
            boxSizing: "border-box",
            minHeight: 0,
            padding: "2px", // Reduced padding to minimize space
          }}
        >
          {loading ? (
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "200px",
                width: "100%",
              }}
            >
              <Text size='sm'>Loading HTML content...</Text>
            </Box>
          ) : (
            <div
              className='html-content'
              style={{
                width: "100%",
                height: "fit-content",
                boxSizing: "border-box",
              }}
              dangerouslySetInnerHTML={{ __html: processedHtml }}
            />
          )}
        </Box>
      </Box>
    </>
  );
};
